/**
 * Small, pure formatters shared across the Home context. No i18n lives here — these
 * return locale-independent strings (initials, a percent, HH:MM). Anything that needs
 * a translated word (weekday names, source labels) resolves at the call site.
 */

/**
 * First letters of the first one or two words, upper-cased. Empty name → "?".
 *
 * `name` is typed as a string and is nonetheless checked, because the types lie at one
 * specific door: route params. `EmployeeReport` declares `name: string`, but its URL is
 * `/reports/employee/:recordId` — a shared or reloaded link carries no name at all, so
 * the screen holds `undefined` while the report loads. That used to render as nothing;
 * once a face went into that header it became a crash on somebody else's link. A
 * fallback disc is the right answer to «имени пока нет» in every one of these places.
 */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const head = parts[0].charAt(0);
  const tail = parts.length > 1 ? parts[1].charAt(0) : '';
  return (head + tail).toUpperCase();
}

/** A similarity/accuracy percent, rounded. null/undefined → an em dash. */
export function formatSimilarity(percent: number | null | undefined): string {
  if (percent == null) {
    return '—';
  }
  return `${Math.round(percent)}%`;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * An access-event timestamp. Today → "HH:MM"; any other day → "DD.MM HH:MM".
 * Deliberately locale-independent (DD.MM is the ru order the app uses everywhere) so
 * it never depends on Hermes shipping ICU data for a given device.
 */
export function formatEventTime(millis: number, now: Date = new Date()): string {
  const d = new Date(millis);
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? time : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)} ${time}`;
}

/** An ISO timestamp (as the gateway serializes last_seen_at etc.) → event-time, or an em dash. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '—' : formatEventTime(ms);
}

/** Minute-of-day (0..1439) → "HH:MM". null/undefined → null (nothing to show). */
export function formatDayMinutes(minutes: number | null | undefined): string | null {
  if (minutes == null) {
    return null;
  }
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
}

/** A wall-clock instant (epoch millis) → "HH:MM". null/undefined → an em dash. */
export function formatClock(millis: number | null | undefined): string {
  if (millis == null) {
    return '—';
  }
  const d = new Date(millis);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * A calendar date (epoch millis) → "DD.MM.YYYY", the ru order the app uses everywhere.
 * Deliberately locale-independent for the same reason as formatEventTime: `toLocaleDateString`
 * silently ignores the 'ru-RU' tag and falls back to US ordering on a device whose Hermes
 * build has no ICU data.
 */
export function formatDate(millis: number): string {
  const d = new Date(millis);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * A duration in minutes → a Russian "Ч ч ММ мин" reading. Locale-independent on
 * purpose (no Intl.RelativeTimeFormat, which needs ICU): the caller passes the ru
 * unit words in. 0/null/undefined → the "0 мин" form via `minLabel` so a worked-less
 * day still reads as a quantity, not a blank.
 */
export function formatHoursFromMinutes(
  minutes: number | null | undefined,
  hourLabel: string,
  minLabel: string,
): string {
  const total = Math.max(0, Math.round(minutes ?? 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) {
    return `${m} ${minLabel}`;
  }
  if (m === 0) {
    return `${h} ${hourLabel}`;
  }
  return `${h} ${hourLabel} ${m} ${minLabel}`;
}

/**
 * Итог за период — в целых часах: «1 791 ч».
 *
 * Отличается от {@link formatHoursFromMinutes} ровно одним и намеренно: минут нет.
 * Восемь минут внутри месячного итога двенадцати человек — это не точность, это две
 * лишние цифры в самом крупном числе на экране; а в колонке таблицы они ещё и делают
 * столбец рваным, хотя колонка существует ровно затем, чтобы сравнивать строки взглядом.
 * Минуты остаются там, где они и правда решают: в дне, в интервале, в среднем за день.
 *
 * Меньше часа — единственное исключение: «0 ч» о смене в сорок минут было бы неправдой,
 * поэтому такой итог печатается по-старому, с минутами.
 */
export function formatHoursTotal(
  minutes: number | null | undefined,
  hourLabel: string,
  minLabel: string,
): string {
  const total = Math.max(0, Math.round(minutes ?? 0));
  if (total < 60) {
    return formatHoursFromMinutes(total, hourLabel, minLabel);
  }
  return `${formatCount(Math.round(total / 60))} ${hourLabel}`;
}

/** Day-of-month from a "YYYY-MM-DD" date, zero-padded ("2026-07-03" → "03"). */
export function formatDayOfMonth(isoDate: string): string {
  const day = isoDate.slice(8, 10);
  return day || isoDate;
}

/**
 * sv_control's workdays_mask, decoded to weekday indices. Bit 0 = Monday …
 * bit 6 = Sunday (matches sv_control attendance.rs `weekday_bit`); the returned
 * indices are 0..6 in that Mon-first order, ready to key ru weekday labels.
 */
export function activeWeekdays(mask: number): number[] {
  const days: number[] = [];
  for (let i = 0; i < 7; i += 1) {
    if ((mask & (1 << i)) !== 0) {
      days.push(i);
    }
  }
  return days;
}

/**
 * A count, grouped for the eye: 1263 → "1 263".
 *
 * The separator is a NARROW NO-BREAK SPACE (U+202F), not a plain space: a plain one
 * lets "1 263" wrap across a line break in the middle of a number, and a comma reads as
 * a decimal point to a Russian or Uzbek user. Small numbers (the common case on a
 * dashboard tile) come back untouched.
 */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.trunc(value)).toString();
  if (digits.length < 4) {
    return sign + digits;
  }
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
}

/**
 * RFC 3339 → «ДД.ММ.ГГГГ» той же формой, что `formatDate`. Нечитаемое — `null`:
 * лучше не показать дату, чем показать «NaN.NaN.NaN».
 */
export function formatIsoDate(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : formatDate(ms);
}
